import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  ArrowUpRight, ArrowDownRight, Target, XCircle, Hourglass, Trophy,
} from "@phosphor-icons/react";
import AppShell from "@/components/AppShell";
import { fetchHistory, fetchHistoryStats, fmtPrice, fmtRelative } from "@/lib/api";
import { AUREUS } from "@/constants/testIds";

const RANGES = [
  { id: "day",   label: "Day" },
  { id: "week",  label: "Week" },
  { id: "month", label: "Month" },
];

const MODE_FILTERS = [
  { id: "all",   label: "All"   },
  { id: "snipe", label: "Trade Zone" },
  { id: "scalp", label: "Short Zone" },
];

const SYMBOL_FILTERS = [
  { id: "all",    label: "All Pairs" },
  { id: "XAUUSD", label: "XAUUSD" },
];

const OUTCOME_META = {
  TP1:     { Icon: Target,    color: "text-bull",     chip: "chip-bull"    },
  TP2:     { Icon: Trophy,    color: "text-bull",     chip: "chip-bull"    },
  SL:      { Icon: XCircle,   color: "text-bear",     chip: "chip-bear"    },
  EXPIRED: { Icon: Hourglass, color: "text-zinc-500", chip: "chip-neutral" },
};

function StatTile({ label, value, accent = "text-white", testId }) {
  return (
    <div className="p-5 border-l first:border-l-0 border-white/10 flex-1">
      <div className="overline">{label}</div>
      <div className={`mono text-2xl md:text-3xl font-semibold tracking-tighter mt-2 ${accent}`} data-testid={testId}>
        {value}
      </div>
    </div>
  );
}

function StatsCard({ stats }) {
  return (
    <div data-testid={AUREUS.historyStatsCard} className="surface grid grid-cols-2 md:grid-cols-4">
      <StatTile
        label="Win Rate"
        value={`${stats.win_rate.toFixed(1)}%`}
        accent={stats.win_rate >= 50 ? "text-bull" : "text-bear"}
        testId={AUREUS.historyWinRate}
      />
      <StatTile
        label="Avg R"
        value={`${stats.avg_rr >= 0 ? "+" : ""}${stats.avg_rr.toFixed(2)}R`}
        accent={stats.avg_rr >= 0 ? "text-bull" : "text-bear"}
        testId={AUREUS.historyAvgRR}
      />
      <StatTile
        label="Closed Setups"
        value={stats.total}
        testId={AUREUS.historyTotal}
      />
      <StatTile
        label="Total R"
        value={`${stats.total_rr >= 0 ? "+" : ""}${stats.total_rr.toFixed(1)}R`}
        accent={stats.total_rr >= 0 ? "text-bull" : "text-bear"}
      />
    </div>
  );
}

function OutcomeBuckets({ buckets }) {
  const data = [
    { name: "TP1",     value: buckets.TP1 || 0,     color: "#10B981" },
    { name: "TP2",     value: buckets.TP2 || 0,     color: "#34D399" },
    { name: "SL",      value: buckets.SL || 0,      color: "#EF4444" },
    { name: "Expired", value: buckets.EXPIRED || 0, color: "#6B7280" },
  ];
  return (
    <div className="surface p-5">
      <div className="overline mb-4">Outcome Distribution</div>
      <ResponsiveContainer width="100%" height={140}>
        <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -25 }}>
          <XAxis dataKey="name" stroke="#52525B" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
          <YAxis stroke="#52525B" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
          <Tooltip
            cursor={{ fill: "rgba(255,255,255,0.04)" }}
            contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0, fontSize: 11, fontFamily: "JetBrains Mono" }}
          />
          <Bar dataKey="value" radius={[2, 2, 0, 0]}>
            {data.map((d) => <Cell key={d.name} fill={d.color} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function HistoryRow({ s }) {
  const meta = OUTCOME_META[s.outcome] || OUTCOME_META.EXPIRED;
  const BiasIcon = s.bias === "BULLISH" ? ArrowUpRight : ArrowDownRight;
  return (
    <tr data-testid={AUREUS.historyRow(s.id)} className="border-b border-white/5 hover:bg-white/[0.02] transition">
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="heading font-semibold text-sm">{s.symbol}</span>
          <BiasIcon size={12} className={s.bias === "BULLISH" ? "text-bull" : "text-bear"} />
        </div>
      </td>
      <td className="px-4 py-3 mono text-xs text-zinc-400 uppercase">{s.mode}</td>
      <td className="px-4 py-3"><span className="chip chip-neutral">T{s.tier}</span></td>
      <td className="px-4 py-3 mono text-xs text-zinc-300">{s.score}</td>
      <td className="px-4 py-3 mono text-xs text-zinc-400">{fmtPrice(s.entry_low)} – {fmtPrice(s.entry_high)}</td>
      <td className="px-4 py-3 mono text-xs text-bear">{fmtPrice(s.sl)}</td>
      <td className="px-4 py-3 mono text-xs text-bull">{fmtPrice(s.t1)} / {fmtPrice(s.t2)}</td>
      <td className="px-4 py-3">
        <span className={`chip ${meta.chip}`}>
          <meta.Icon size={11} weight="bold" /> {s.outcome}
        </span>
      </td>
      <td className={`px-4 py-3 mono text-sm ${s.rr_realized >= 0 ? "text-bull" : "text-bear"}`}>
        {s.rr_realized != null ? `${s.rr_realized >= 0 ? "+" : ""}${s.rr_realized.toFixed(1)}R` : "—"}
      </td>
      <td className="px-4 py-3 mono text-xs text-zinc-500 whitespace-nowrap">{fmtRelative(s.closed_at)}</td>
    </tr>
  );
}

export default function HistoryPage() {
  const [range, setRange] = useState("month");
  const [modeFilter, setModeFilter] = useState("all");
  const [symbolFilter, setSymbolFilter] = useState("all");

  const apiParams = useMemo(() => {
    const p = { range };
    if (modeFilter !== "all") p.mode = modeFilter;
    if (symbolFilter !== "all") p.symbol = symbolFilter;
    return p;
  }, [range, modeFilter, symbolFilter]);

  const { data: history, isLoading: lHist } = useQuery({
    queryKey: ["history", apiParams],
    queryFn: () => fetchHistory(apiParams),
    refetchInterval: 8000,
  });
  const { data: stats, isLoading: lStats } = useQuery({
    queryKey: ["history-stats", apiParams],
    queryFn: () => fetchHistoryStats(apiParams),
    refetchInterval: 8000,
  });

  return (
    <AppShell>
      <div className="p-6 md:p-10" data-testid={AUREUS.historyPage}>
        <header className="flex items-end justify-between gap-6 flex-wrap mb-6">
          <div>
            <div className="overline mb-3">Trade History · 90-day retention</div>
            <h1 className="heading text-3xl md:text-5xl font-bold tracking-tighter">
              Receipts. <span className="text-gold-grad">All of them.</span>
            </h1>
            {symbolFilter !== "all" && (
              <p className="text-zinc-400 mt-3 text-sm mono">
                Filtered to <span className="text-gold">{symbolFilter}</span> only
              </p>
            )}
          </div>
        </header>

        {/* Symbol selector — one row, scrollable on mobile */}
        <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1">
          {SYMBOL_FILTERS.map((s) => (
            <button
              key={s.id}
              type="button"
              data-testid={AUREUS.historySymbolBtn(s.id)}
              onClick={() => setSymbolFilter(s.id)}
              className={`px-4 py-2 mono text-[11px] uppercase tracking-widest border whitespace-nowrap transition ${
                symbolFilter === s.id
                  ? "border-gold/60 bg-gold/10 text-gold"
                  : "border-white/10 text-zinc-400 hover:text-white hover:bg-white/[0.03]"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div data-testid={AUREUS.historyFilters} className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2">
            {RANGES.map((r) => (
              <button
                key={r.id}
                type="button"
                data-testid={AUREUS.historyRangeBtn(r.id)}
                onClick={() => setRange(r.id)}
                className={`px-3 py-1.5 mono text-[11px] uppercase tracking-widest border transition ${
                  range === r.id ? "border-gold/60 bg-gold/10 text-gold" : "border-white/10 text-zinc-400 hover:text-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {MODE_FILTERS.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setModeFilter(m.id)}
                className={`px-3 py-1.5 mono text-[11px] uppercase tracking-widest border transition ${
                  modeFilter === m.id ? "border-white/30 bg-white/5 text-white" : "border-white/10 text-zinc-400 hover:text-white"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {lStats && <div className="surface h-28 animate-pulse mb-6" />}
        {stats && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <div className="lg:col-span-2"><StatsCard stats={stats} /></div>
            <OutcomeBuckets buckets={stats.buckets || {}} />
          </div>
        )}

        {lHist && <div className="surface h-96 animate-pulse" />}
        {history?.setups && (
          <div className="surface overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/10 text-zinc-500">
                  {["Pair", "Mode", "Tier", "Score", "Entry", "SL", "Targets", "Outcome", "R", "Closed"].map((h) => (
                    <th key={h} className="px-4 py-3 overline">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.setups.length === 0 ? (
                  <tr><td colSpan={10} className="px-4 py-10 text-center text-zinc-500 text-sm">No closed setups in this range.</td></tr>
                ) : history.setups.map((s) => <HistoryRow key={s.id} s={s} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
